import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { API_BASE_URL } from '../config';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Downtime = () => {
    const { url } = useParams();
    const [downtimes, setDowntimes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = `Downtime - ${url}`;
        fetchDowntime();
    }, [url]);

    const fetchDowntime = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/get_website_downtime/${encodeURIComponent(url)}`);
            if (response.data.status === 'ok') {
                setDowntimes(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching downtime data', error);
        } finally {
            setLoading(false);
        }
    };

    const processChartData = (dataList) => {
        let maxTime = new Date().getTime();
        const thirtyDaysAgo = maxTime - (30 * 24 * 60 * 60 * 1000);
        let actualMinTime = thirtyDaysAgo;

        const start = new Date(actualMinTime);
        start.setMinutes(0, 0, 0);

        const labels = [];
        const data = [];

        // 1. Sort events by time ascending
        const sortedEvents = [...(dataList || [])].sort((a, b) => new Date(a.check_time) - new Date(b.check_time));

        // 2. Determine initial status at start time
        // Find the most recent event before the start time
        let currentStatus = 0; // Default to OK (0)
        for (const event of sortedEvents) {
            if (new Date(event.check_time) < start) {
                currentStatus = event.name === 'Site indisponible' ? 1 : 0;
            } else {
                break;
            }
        }

        // 3. Loop hour by hour
        let current = new Date(start);
        let eventIdx = 0;
        // Skip events before start time as they were used for initial status
        while (eventIdx < sortedEvents.length && new Date(sortedEvents[eventIdx].check_time) < start) {
            eventIdx++;
        }

        while (current.getTime() <= maxTime) {
            const dateStr = current.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' });
            const timeStr = current.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
            labels.push(`${dateStr} ${timeStr}`);

            // Find events that happened in THIS hour
            const nextHour = new Date(current.getTime() + 60 * 60 * 1000);
            let hourHadDownEvent = false;
            let hourHadUpEvent = false;

            while (eventIdx < sortedEvents.length && new Date(sortedEvents[eventIdx].check_time) < nextHour) {
                if (sortedEvents[eventIdx].name === 'Site indisponible') {
                    currentStatus = 1;
                    hourHadDownEvent = true;
                } else if (sortedEvents[eventIdx].name === 'Site en ligne') {
                    currentStatus = 0;
                    hourHadUpEvent = true;
                }
                eventIdx++;
            }

            // Representation choice: If the hour was DOWN at ANY point, mark it as DOWN (1) 
            // OR if it's currently DOWN.
            if (currentStatus === 1 || hourHadDownEvent) {
                data.push(1);
            } else {
                data.push(0);
            }

            current = nextHour;
        }

        // Add final point for "Now" to ensure visibility up to the current moment
        const dateStr = new Date(maxTime).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit' });
        const timeStr = new Date(maxTime).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
        labels.push(`${dateStr} ${timeStr}`);
        data.push(currentStatus);

        return {
            labels,
            datasets: [
                {
                    label: 'Statut du site',
                    data: data,
                    backgroundColor: 'rgba(239, 68, 68, 0.2)', // light red
                    borderColor: 'rgb(239, 68, 68)',
                    borderWidth: 2,
                    fill: 'origin',
                    stepped: 'middle',
                    pointRadius: 0,
                    pointHitRadius: 10,
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: `Chronologie d'indisponibilité pour ${url}`,
                font: { size: 16 }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return context.parsed.y === 1 ? 'Hors ligne' : 'En ligne';
                    }
                }
            }
        },
        scales: {
            y: {
                min: 0,
                max: 1.1,
                ticks: {
                    stepSize: 1,
                    callback: function (value) {
                        if (value === 0) return 'OK';
                        if (value === 1) return 'DOWN';
                        return '';
                    }
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Heures / Jours'
                },
                ticks: {
                    maxTicksLimit: 24, // Display a reasonable number of X axis labels
                    maxRotation: 45,
                    minRotation: 45
                }
            }
        }
    };

    return (
        <div className="container mx-auto p-20">
            <div className="display-flex mb-20 flex-direction-column flex-start">
                <h2>Rapport d'indisponibilité</h2>
            </div>

            <button className="button-third mb-20" onClick={() => window.close()}>Retour</button>

            <div className="card bg-white p-20 border-radius-5 shadow-sm">
                <h3 className="mb-20 color-third">{url}</h3>

                {loading ? (
                    <div className="text-center p-20">Chargement des données...</div>
                ) : (
                    <div style={{ height: '500px', width: '100%' }}>
                        <Line data={processChartData(downtimes)} options={chartOptions} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Downtime;
